import { FilterOptionsState } from '@mui/base/AutocompleteUnstyled/useAutocomplete';
import { createFilterOptions } from '@mui/material';
import React, { useMemo } from 'react';

const filter = createFilterOptions();

const getLabel = (getOptionLabel, option) =>
  typeof getOptionLabel === 'function' ? getOptionLabel(option) : option[getOptionLabel ?? 'label'];

interface IUseWsAutocomplete {
  <T>(
    getOptionLabel?: string | ((option: T) => string),
    creatable?: boolean,
    persistentAddOption?: boolean,
    label?: string
  ): {
    getOptionLabel: (option: T) => string;
    filterOptions: (options: T[], state: FilterOptionsState<T>) => T[];
    renderOption: (props: React.HTMLAttributes<HTMLLIElement>, option: T) => React.ReactNode;
  };
}

const useWsAutocomplete: IUseWsAutocomplete = <T,>(
  getOptionLabel,
  creatable = false,
  persistentAddOption = false,
  label
) => {
  const autocompleteProps = useMemo(
    () => ({
      getOptionLabel: (option: T) => {
        if (typeof option === 'string') {
          return option;
        }
        return getLabel(getOptionLabel, option);
      },
      filterOptions: (options, params) => {
        const filtered = filter(options, params);

        const { inputValue } = params;

        const isExisting = options.some(
          (option) => inputValue === getLabel(getOptionLabel, option)
        );
        if (creatable && (persistentAddOption || (inputValue !== '' && !isExisting))) {
          filtered.push({
            inputValue: inputValue ?? '',
            ...(typeof getOptionLabel === 'string' && { [getOptionLabel]: `+ Add ${label}` }),
          });
        }

        return filtered as [];
      },
      renderOption: (props, option) => (
        <li {...props}>
          {option.inputValue ? `+ Add ${label}` : getLabel(getOptionLabel, option)}
        </li>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [creatable, getOptionLabel]
  );

  return autocompleteProps;
};

export default useWsAutocomplete;
